import { useMemo } from 'react';
import { FloatingPanelRegisterFn, FloatingPanelUnregisterFn } from './types';
import { useMutableCallback } from '@slope/hooks';
import { FloatingPanelContext, FloatingPanelContextType } from './FloatingPanelContext';
import { useFrame } from '@react-three/fiber';
import { FLOATING_PANEL_STRATEGY_MAP } from './constants';
import { ExtendedNode, ExtendedNodeOptions } from './ForceLayoutCollision';

export interface FloatingPanelProviderProps {
  children: React.ReactNode;
  strategy?: keyof typeof FLOATING_PANEL_STRATEGY_MAP;
}

// FloatingPanelProvider Component
export const FloatingPanelProvider: React.FC<FloatingPanelProviderProps> = ({
  children,
  strategy = 'forceLayout',
}) => {
  const collisionStrategy = useMemo(() => new FLOATING_PANEL_STRATEGY_MAP[strategy](), [strategy]);

  const unregisterPanel = useMutableCallback<FloatingPanelUnregisterFn>((id) => {
    collisionStrategy.delete(id);
  });

  const registerPanel = useMutableCallback<FloatingPanelRegisterFn<ExtendedNodeOptions>>(
    (id, element, options) => {
      collisionStrategy.add(id, element, options);
    },
  );

  const value = useMemo<FloatingPanelContextType<ExtendedNode, ExtendedNodeOptions>>(
    () => ({
      registerPanel,
      unregisterPanel,
      collisionStrategy,
    }),
    [registerPanel, unregisterPanel, collisionStrategy],
  );

  useFrame((state, delta) => {
    collisionStrategy.step(state, delta);
  });

  return (
    <FloatingPanelContext.Provider value={value as never}>{children}</FloatingPanelContext.Provider>
  );
};
